import { getFitViewport, pointToFlow, zoomAroundPoint, ZOOM_STEP } from '../core';
import type { FitViewOptions, Node, Viewport, XYPosition } from '../core';
import type { MindmapStoreApi } from '../store';

/** Animation options are accepted for v11 compatibility; changes are applied at once (the app passes 0). */
type AnimationOptions = { duration?: number };

/** What `onInit` receives and `useCustomMindmap()` returns — the instance methods the app uses. */
export type CustomMindmapInstance = {
  /** Point relative to the canvas → flow position (v11 `project`). */
  project: (point: XYPosition) => XYPosition;
  /** Screen (client) point → flow position (v12 name, kept as an alias). */
  screenToFlowPosition: (point: XYPosition) => XYPosition;
  getNodes: () => Node[];
  getViewport: () => Viewport;
  setViewport: (viewport: Viewport, options?: AnimationOptions) => void;
  /** Zooms in one step (×1.2) around the pane centre, within the zoom limits. */
  zoomIn: (options?: AnimationOptions) => void;
  /** Zooms out one step (÷1.2) around the pane centre, within the zoom limits. */
  zoomOut: (options?: AnimationOptions) => void;
  /** Zooms to `zoomLevel` around the pane centre, within the zoom limits (v11). */
  zoomTo: (zoomLevel: number, options?: AnimationOptions) => void;
  /** Fits all visible nodes; false when nothing is measured yet (v11). */
  fitView: (options?: FitViewOptions & AnimationOptions) => boolean;
};

/** Nodes with the sizes the engine measured, so fitting works even before the app applies dimension changes. */
const measuredNodes = (store: MindmapStoreApi): Node[] => {
  const { nodes, nodeLookup } = store.getState();
  return nodes.map((node) => {
    const internal = nodeLookup.get(node.id);
    return { ...node, width: internal?.width ?? node.width, height: internal?.height ?? node.height };
  });
};

const HALF = 0.5;

/** Zooms to `zoom` around the pane centre, within the zoom limits. */
const zoomToLevel = (store: MindmapStoreApi, zoom: number) => {
  const { viewport, paneSize, minZoom, maxZoom, setViewport } = store.getState();
  const centre = { x: paneSize.width * HALF, y: paneSize.height * HALF };
  setViewport(zoomAroundPoint(viewport, centre, zoom, { minZoom, maxZoom }));
};

const zoomByStep = (store: MindmapStoreApi, factor: number) => zoomToLevel(store, store.getState().viewport.zoom * factor);

/** Instance methods read the store when called, so one instance stays valid for the canvas lifetime. */
export const createInstance = (store: MindmapStoreApi): CustomMindmapInstance => ({
  project: (point) => pointToFlow(point, store.getState().viewport),
  screenToFlowPosition: ({ x, y }) => {
    const { domNode, viewport } = store.getState();
    const bounds = domNode?.getBoundingClientRect();
    return pointToFlow({ x: x - (bounds?.left ?? 0), y: y - (bounds?.top ?? 0) }, viewport);
  },
  getNodes: () => store.getState().nodes.map((node) => ({ ...node })),
  getViewport: () => ({ ...store.getState().viewport }),
  setViewport: (viewport) => store.getState().setViewport(viewport),
  zoomIn: () => zoomByStep(store, ZOOM_STEP),
  zoomOut: () => zoomByStep(store, 1 / ZOOM_STEP),
  zoomTo: (zoom) => zoomToLevel(store, zoom),
  fitView: (options) => {
    const { paneSize, minZoom, maxZoom, setViewport } = store.getState();
    const viewport = getFitViewport(measuredNodes(store), paneSize, { minZoom, maxZoom }, options);
    if (!viewport) return false;
    setViewport(viewport);
    return true;
  },
});
